import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface User {
  username: string;
  email: string;
  user_id: string;
  role: string;
  category: string;
  first_name: string;
  last_name: string;
  location: string;
  discount_percent: number;
}

export interface PermissionMap {
  [key: string]: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  permissions: PermissionMap;
  isAuthenticated: boolean;
  sessionExpiresAt: number | null;
}

const storedUser = localStorage.getItem("user");

const storedPermissions = localStorage.getItem("permissions");

const storedToken = localStorage.getItem("token");

const storedSessionExpiresAt = localStorage.getItem("sessionExpiresAt");

const sessionExpiresAt = storedSessionExpiresAt
  ? Number(storedSessionExpiresAt)
  : null;

const sessionStillValid =
  sessionExpiresAt !== null && sessionExpiresAt > Date.now();

const initialState: AuthState = {
  user: storedUser && sessionStillValid ? JSON.parse(storedUser) : null,

  token: storedToken && sessionStillValid ? storedToken : null,

  permissions:
    storedPermissions && sessionStillValid ? JSON.parse(storedPermissions) : {},

  isAuthenticated: Boolean(storedToken) && sessionStillValid,

  sessionExpiresAt: sessionStillValid ? sessionExpiresAt : null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Stores a newly authenticated session.
     */
    loginSuccess: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        permissions: PermissionMap;
        sessionExpiresAt: number;
      }>,
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.permissions = action.payload.permissions;
      state.sessionExpiresAt = action.payload.sessionExpiresAt;
      state.isAuthenticated = true;

      localStorage.setItem("user", JSON.stringify(action.payload.user));

      localStorage.setItem("token", action.payload.token);

      localStorage.setItem(
        "permissions",
        JSON.stringify(action.payload.permissions),
      );

      localStorage.setItem(
        "sessionExpiresAt",
        String(action.payload.sessionExpiresAt),
      );
    },

    /**
     * Replaces only the short-lived access token.
     * The 24-hour session expiry remains unchanged.
     */
    tokenRefreshed: (
      state,
      action: PayloadAction<{
        token: string;
        permissions?: PermissionMap;
        user?: User;
      }>,
    ) => {
      state.token = action.payload.token;
      state.isAuthenticated = true;

      localStorage.setItem("token", action.payload.token);

      if (action.payload.permissions) {
        state.permissions = action.payload.permissions;

        localStorage.setItem(
          "permissions",
          JSON.stringify(action.payload.permissions),
        );
      }

      if (action.payload.user) {
        state.user = action.payload.user;

        localStorage.setItem("user", JSON.stringify(action.payload.user));
      }
    },

    /**
     * Clears all local authentication state.
     */
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.permissions = {};
      state.sessionExpiresAt = null;
      state.isAuthenticated = false;

      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("permissions");
      localStorage.removeItem("sessionExpiresAt");
    },
  },
});

export const { loginSuccess, tokenRefreshed, logout } = authSlice.actions;

export default authSlice.reducer;
